import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type DocumentCandidateType = 'project' | 'portfolio' | 'strategy' | 'budget';
export type DocumentCandidateStatus = 'suggested' | 'approved' | 'rejected' | 'modified';
export type DocumentReviewAction = 'approve' | 'reject' | 'modify';

export interface DocumentCandidate {
  id: string;
  tenant_id: string;
  source_document_id: string | null;
  candidate_type: DocumentCandidateType;
  name: string;
  description: string | null;
  detected_budget: number | null;
  detected_start_date: string | null;
  detected_end_date: string | null;
  detected_portfolio: string | null;
  matched_project_id: string | null;
  match_confidence: number | null;
  status: DocumentCandidateStatus;
  raw_payload: Record<string, unknown> | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  proposal_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentCandidateEdits {
  name?: string;
  description?: string | null;
  detected_budget?: number | null;
  detected_start_date?: string | null;
  detected_end_date?: string | null;
  detected_portfolio?: string | null;
}

interface SubmitReviewParams {
  id: string;
  // The candidate's own candidate_type, supplied by the caller (mirrors
  // useActionItems' currentStatus precedent) rather than looked up via
  // `items.find(...)` here — that would force `items` into this
  // callback's deps and re-create submitReview on every realtime tick.
  // Reviewer-overridden value: pass a different value than the item's
  // current candidate_type to reclassify it (e.g. AI suggested
  // 'strategy', reviewer confirms it's actually 'portfolio') before
  // ingestion — persisted alongside the status update.
  candidateType: DocumentCandidateType;
  action: DocumentReviewAction;
  notes: string;
  edits?: DocumentCandidateEdits;
  // Explicitly provided by the reviewer to confirm/override the AI's
  // suggested match, or `null` for "create as new project". `undefined`
  // (the key omitted entirely) means "leave matched_project_id as-is" —
  // this MUST be distinguished from `null`, since the ingestion RPC
  // branches on `matched_project_id IS NOT NULL` to decide whether to
  // enrich an existing projects_sync row or mint a new one.
  linkToProject?: string | null;
  // 'budget' candidates require this (single project, reuses
  // linkToProject); 'portfolio' candidates require this instead
  // (multiple projects the portfolio tag gets stamped onto).
  linkToProjects?: string[];
}

type SubmitReviewResult =
  | { ok: true; ingested: boolean; ingestError?: string }
  | { ok: false; error: string };

// 'modify' is itself an approval, same convention as useActionItems:
// editing an AI-suggested candidate and saving transitions it straight
// to 'approved' (applying edits at the same time), not to a separate
// terminal 'modified' bucket. 'modified' remains a valid status value
// for historical rows, but no UI action writes it anymore.
const STATUS_BY_ACTION: Record<DocumentReviewAction, DocumentCandidateStatus> = {
  approve: 'approved',
  reject: 'rejected',
  modify: 'approved',
};

export const useDocumentCandidates = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<DocumentCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('document_candidates')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error) {
      console.error('Failed to load document candidates:', error);
    } else {
      setItems((data as DocumentCandidate[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('document-candidates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'document_candidates' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  const submitReview = useCallback(
    async (params: SubmitReviewParams): Promise<SubmitReviewResult> => {
      if (!user) return { ok: false, error: 'Not authenticated' };
      setSubmitting(true);

      const newStatus = STATUS_BY_ACTION[params.action];

      // Step 1: update document_candidates' own status. This MUST
      // complete (and succeed) before the ingestion RPC is ever called —
      // ingest_document_candidate's server-side guard RAISE EXCEPTIONs
      // unless status IN ('approved', 'modified'), so calling the RPC
      // concurrently with, or before, this update fails every time.
      const { error } = await supabase
        .from('document_candidates')
        .update({
          status: newStatus,
          candidate_type: params.candidateType,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          review_notes: params.notes || null,
          ...(params.action === 'modify' && params.edits ? params.edits : {}),
          // Only touch matched_project_id if the caller explicitly
          // provided it (including explicit `null` for "create as new
          // project") — omitting the key entirely leaves the AI's
          // suggested match untouched.
          ...('linkToProject' in params ? { matched_project_id: params.linkToProject } : {}),
        })
        .eq('id', params.id);

      if (error) {
        setSubmitting(false);
        return { ok: false, error: error.message };
      }

      // Step 2: only after the status update above has succeeded, and
      // only for approve/modify — the ingestion RPC now supports all
      // four candidate_type values (project/portfolio/budget/strategy).
      const shouldIngest =
        (params.action === 'approve' || params.action === 'modify') &&
        ['project', 'portfolio', 'budget', 'strategy'].includes(params.candidateType);

      if (!shouldIngest) {
        setSubmitting(false);
        return { ok: true, ingested: false };
      }

      const { error: rpcError } = await supabase.rpc('ingest_document_candidate', {
        candidate_id: params.id,
        project_ids: params.candidateType === 'portfolio' ? params.linkToProjects ?? null : null,
      });

      setSubmitting(false);

      if (rpcError) {
        // The status update already succeeded — the review is saved,
        // but ingestion into projects_sync did not happen. Surface this
        // as a distinct partial-failure state rather than silently
        // losing the candidate or reporting a plain error (which would
        // wrongly imply the review itself failed).
        return { ok: true, ingested: false, ingestError: rpcError.message };
      }

      return { ok: true, ingested: true };
    },
    [user],
  );

  return { items, loading, submitting, submitReview, reload: load };
};
